// Shared consent constants, text, types, and version hashing.
// Used by both client (forms) and server (audit log + Zod schema) — moved
// from website/src/utils/consents.ts so @lc/lenacareer-ai-bot's Mini App
// registration route can build the same audit rows without forking the
// lawyer-approved text (root AGENTS.md: extract a shared core both wrap).
//
// Text strings are BYTE-IDENTICAL to what shipped before the move: they are
// the lawyer-approved wording and the FNV-1a hash of them is the
// `consent_version` already recorded in prod rows — changing a single
// character silently forks the audit history.
export const CONSENT_LINKS = {
    policy: "https://lenacareer.school/policy",
    personalDataAgreement: "https://lenacareer.school/personal-data-agreement",
    advAgreement: "https://lenacareer.school/adv-agreement",
};
// Canonical plain-text strings used for the version hash AND as the
// `consent_text` value written to PG. Links are listed inline as
// "[label](url)" so the hash captures both wording and target URLs.
//
// Per 152-ФЗ (01.09.2025): personal-data consent must be a standalone
// statement — kept in its own descriptor, not bundled with adv_mailing.
export const FREE_FORM_CONSENTS = [
    {
        type: "personal_data",
        text: "Нажимая на кнопку, я даю [согласие](https://lenacareer.school/personal-data-agreement) на обработку моих персональных данных в соответствии с [Политикой](https://lenacareer.school/policy).",
        links: {
            policy: CONSENT_LINKS.policy,
            personalDataAgreement: CONSENT_LINKS.personalDataAgreement,
        },
    },
    {
        type: "adv_mailing",
        text: "Нажимая на кнопку, я даю [согласие](https://lenacareer.school/adv-agreement) на получение информационной и рекламной рассылки.",
        links: {
            advAgreement: CONSENT_LINKS.advAgreement,
        },
    },
];
export function buildPaidFormConsents(args) {
    return {
        personalData: {
            type: "personal_data",
            text: "Нажимая на кнопку, я даю [согласие](https://lenacareer.school/personal-data-agreement) на обработку моих персональных данных в соответствии с [Политикой](https://lenacareer.school/policy).",
            links: {
                policy: CONSENT_LINKS.policy,
                personalDataAgreement: CONSENT_LINKS.personalDataAgreement,
            },
        },
        advMailing: {
            type: "adv_mailing",
            text: "Нажимая на кнопку, я даю [согласие](https://lenacareer.school/adv-agreement) на получение информационной и рекламной рассылки.",
            links: {
                advAgreement: CONSENT_LINKS.advAgreement,
            },
        },
        oferta: {
            type: "oferta",
            text: `Нажимая на кнопку я принимаю условия [Публичной оферты](${args.ofertaUrl}) на ${args.ofertaProductName}`,
            links: {
                oferta: args.ofertaUrl,
            },
        },
    };
}
// Lightweight FNV-1a 64-bit hash, hex-encoded. We don't need
// cryptographic strength — only a stable fingerprint of the exact text
// and URLs shown to the user, computable in browser without subtle/crypto
// async API. Same input ⇒ same output across client and server.
export function hashConsentVersion(descriptor) {
    const payload = JSON.stringify({
        type: descriptor.type,
        text: descriptor.text,
        links: descriptor.links,
    });
    return fnv1a64Hex(payload);
}
export function fnv1a64Hex(input) {
    // FNV-1a 64-bit. Use BigInt to avoid JS number precision issues.
    let hash = 0xcbf29ce484222325n;
    const prime = 0x100000001b3n;
    const mask = 0xffffffffffffffffn;
    for (let i = 0; i < input.length; i++) {
        hash ^= BigInt(input.charCodeAt(i));
        hash = (hash * prime) & mask;
    }
    return hash.toString(16).padStart(16, "0");
}
export function buildConsentRecord(descriptor, acceptedAt = new Date()) {
    return {
        type: descriptor.type,
        version: hashConsentVersion(descriptor),
        text: descriptor.text,
        links: descriptor.links,
        acceptedAt: acceptedAt.toISOString(),
    };
}
// --- DB half -----------------------------------------------------------
//
// Pure — no `pg`, no `node:*` imports: this module is bundled into the
// website's browser build (website/src/utils/consents.ts re-exports it), so
// it can only hold the SQL text + the row → params mapping. Each consumer
// (website/server/consents-db.ts, lenacareer-ai-bot/src/miniapp/routes.ts)
// owns its own `pg.Pool`/transaction and imports these from here instead of
// declaring its own copy.
export const CONSENT_INSERT_SQL = `INSERT INTO consents (
  accepted_at, email, name, form_id, form_type, consent_type,
  consent_version, consent_text, consent_links, page_url, ip,
  user_agent, request_id, metadata
) VALUES ($1,$2,$3,$4,$5,$6,$7,$8,$9::jsonb,$10,$11,$12,$13,$14::jsonb)`;
export function consentInsertParams(r) {
    return [
        r.acceptedAt,
        r.email,
        r.name,
        r.formId,
        r.formType,
        r.consentType,
        r.consentVersion,
        r.consentText,
        JSON.stringify(r.consentLinks),
        r.pageUrl,
        r.ip,
        r.userAgent,
        r.requestId,
        r.metadata ? JSON.stringify(r.metadata) : null,
    ];
}
//# sourceMappingURL=consents.js.map